import { NoMailItemError, logError } from '@utils-global';
import {
  GraphMimeError,
  GraphMimeFailureCause,
  classifyGraphMimeFailure,
  isExpectedGraphMimeFailure,
} from './classifyGraphMimeFailure';
import { getMailMIME } from './getMailMIME';

const TOAST_BY_CAUSE: Record<GraphMimeFailureCause, string> = {
  itemNotFound: 'errors.emailWasDeleted',
  invalidId: 'errors.mimeInvalidId',
  accessDenied: 'errors.mimeAccessDenied',
  sessionExpired: 'errors.mimeSessionExpired',
  mailboxUnavailable: 'errors.mimeMailboxUnavailable',
  serverBusy: 'errors.mimeServerBusy',
  mimeGeneration: 'errors.mimeGenerationFailed',
  offline: 'errors.offline',
  graphError: 'errors.mime',
  unknown: 'errors.mimeUnknown',
};

type FetchGraphMimeParams = {
  mailId: string | undefined;
  mailboxEmail: string;
  isSharedMailbox: boolean;
};

export const fetchGraphMime = async (
  { mailId, mailboxEmail, isSharedMailbox }: FetchGraphMimeParams,
  showToast: (toastKey: string) => void,
): Promise<string | undefined> => {
  if (!mailId) {
    throw new NoMailItemError('Could not retrieve mail identifier');
  }

  try {
    const mimeContent = await getMailMIME(mailId, mailboxEmail, isSharedMailbox);

    if (!mimeContent.data) {
      logError('Microsoft Graph returned an empty MIME body', { mailId });
      showToast('errors.mimeUnknown');
      return undefined;
    }

    return mimeContent.data;
  } catch (error) {
    const diagnosis = classifyGraphMimeFailure(error);

    logError('Failed to get MIME content from Microsoft Graph', {
      cause: diagnosis.cause,
      errorCode: diagnosis.errorCode,
      errorMessage: diagnosis.errorMessage,
      statusCode: diagnosis.statusCode,
      error,
    });

    showToast(TOAST_BY_CAUSE[diagnosis.cause] ?? 'errors.mime');

    if (!isExpectedGraphMimeFailure(diagnosis.cause)) {
      throw new GraphMimeError(`Graph getMailMIME failed (cause: ${diagnosis.cause})`, diagnosis);
    }

    return undefined;
  }
};
